import React, { useEffect, useMemo, useState } from "react";
import "@apa/core-renderers/styles.css";
import type { IconType } from "react-icons";
import { FiInfo, FiMaximize, FiMonitor, FiSmartphone, FiTablet } from "react-icons/fi";
import type { SiteSettingsPayload, SiteSettings as SiteSettingsModel } from "../../types/layout";
import { Modal } from "../Modal";
import LayoutPreviewSurface from "./LayoutPreviewSurface";
import type { LanguageOption } from "@apa/core-renderers";
import {
	BREAKPOINTS,
	DEFAULT_LANG,
	buildFooterData,
	buildHeaderData,
	buildLanguageOptions,
	type DisplayViewportKey
} from "./previewUtils";
import { client } from "../../Clients";
import { buildLayoutPayloadFromSettings, sanitizeLayoutSettings } from "./utils";

interface LayoutPreviewModalProps {
	show: boolean;
	onClose: () => void;
	initialLocale?: string;
	disabled?: boolean;
	payloadOverride?: SiteSettingsPayload | null;
	localeOverride?: string;
	languageOptionsOverride?: LanguageOption[];
	headerOverride?: ReturnType<typeof buildHeaderData> | null;
	footerOverride?: ReturnType<typeof buildFooterData> | null;
}

type PreviewViewportKey = "full" | "mobile" | "medium" | "desktop";

type ViewportConfig = {
	label: string;
	helper: string;
	width: number | null;
	icon: IconType;
};

const PREVIEW_VIEWPORTS: Record<PreviewViewportKey, ViewportConfig> = {
	mobile: {
		label: "Mobile",
		helper: "Phones · 360-428px",
		width: 428,
		icon: FiSmartphone
	},
	medium: {
		label: "Medium",
		helper: "Tablets · 960px",
		width: 960,
		icon: FiTablet
	},
	desktop: {
		label: "Desktop",
		helper: "Large screens · 1280px",
		width: 1280,
		icon: FiMonitor
	},
	full: {
		label: "Full width",
		helper: "Stretch to modal",
		width: null,
		icon: FiMaximize
	}
};

const VIEWPORT_ORDER: PreviewViewportKey[] = ["mobile", "medium", "desktop", "full"];

const FULL_WIDTH_RANGE = {
	min: 320,
	max: 1600,
	step: 20
} as const;

type MobilePresetKey = "compact" | "standard" | "plus";

type MobilePresetConfig = {
	label: string;
	helper: string;
	width: number;
};

const MOBILE_PHONE_PRESETS: Record<MobilePresetKey, MobilePresetConfig> = {
	compact: { label: "Compact", helper: "360px · Small Android", width: 360 },
	standard: { label: "Modern", helper: "390px · iPhone 14", width: 390 },
	plus: { label: "Spacious", helper: "428px · Pro Max", width: 428 }
};

const DEFAULT_MOBILE_PRESET: MobilePresetKey = "standard";

const cx = (...classes: Array<string | false | null | undefined>) => classes.filter(Boolean).join(" ");

const normalizeCode = (value?: string | null) => (value ?? "").trim().toLowerCase();

type LanguagesContent = {
	languages?: Array<{ code?: string | null; label?: string | null; isDisabled?: boolean | null }>;
	defaultLanguageCode?: string | null;
};

const buildOptionsFromLanguages = (content?: LanguagesContent | null): LanguageOption[] => {
	const list = content?.languages ?? [];
	return list
		.filter((item) => !item.isDisabled)
		.map((item) => {
			const code = normalizeCode(item.code);
			const label = (item.label || item.code || "").toString().toUpperCase();
			return code ? { code, label } : null;
		})
		.filter((x): x is LanguageOption => !!x);
};

/** Layout preview modal with responsive simulation (mobile, tablet, desktop). */
const LayoutPreviewModal: React.FC<LayoutPreviewModalProps> = ({
	show,
	onClose,
	initialLocale,
	disabled,
	payloadOverride,
	localeOverride,
	languageOptionsOverride,
	headerOverride,
	footerOverride
}) => {
	const [locale, setLocale] = useState<string>((initialLocale || DEFAULT_LANG).toLowerCase());
	const [payload, setPayload] = useState<SiteSettingsPayload | null>(payloadOverride ?? null);
	const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(() =>
		languageOptionsOverride ?? buildLanguageOptions(locale || DEFAULT_LANG)
	);
	const [loading, setLoading] = useState(false);
	const [viewport, setViewport] = useState<PreviewViewportKey>("full");
	const [mobilePreset, setMobilePreset] = useState<MobilePresetKey>(DEFAULT_MOBILE_PRESET);
	const [fullWidthPixels, setFullWidthPixels] = useState<number>(FULL_WIDTH_RANGE.max);
	const [fullWidthAuto, setFullWidthAuto] = useState(true);

	const headerData = useMemo(
		() => headerOverride ?? buildHeaderData(payload?.header),
		[headerOverride, payload]
	);
	const footerData = useMemo(
		() => footerOverride ?? buildFooterData(payload?.footer),
		[footerOverride, payload]
	);
	const previewReady = headerData && footerData;
	const viewportWidth = useMemo<number | null>(() => {
		if (viewport === "mobile") {
			return MOBILE_PHONE_PRESETS[mobilePreset].width;
		}
		if (viewport === "full") {
			return fullWidthAuto ? null : fullWidthPixels;
		}
		return PREVIEW_VIEWPORTS[viewport].width;
	}, [viewport, mobilePreset, fullWidthAuto, fullWidthPixels]);
	const viewportWidthLabel = useMemo(() => {
		if (viewport === "full") {
			return fullWidthAuto ? "Full width" : `${fullWidthPixels}px wide`;
		}
		return viewportWidth ? `${viewportWidth}px wide` : "Full width";
	}, [viewport, fullWidthAuto, fullWidthPixels, viewportWidth]);
	const isMobileViewport = viewport === "mobile";
	const isFullViewport = viewport === "full";
	const displayViewport = useMemo<DisplayViewportKey>(() => {
		if (viewport === "mobile") return "mobile";
		if (viewport === "medium") return "medium";
		if (viewport === "desktop") return "desktop";
		if (!fullWidthAuto) {
			if (fullWidthPixels < BREAKPOINTS.mobile) return "mobile";
			if (fullWidthPixels < BREAKPOINTS.medium) return "medium";
		}
		return "desktop";
	}, [viewport, fullWidthAuto, fullWidthPixels]);

	useEffect(() => {
		if (!show) return;

		if (payloadOverride) {
			const overrideLocale =
				localeOverride ||
				(payloadOverride as any)?.language?.toString().toLowerCase() ||
				(initialLocale || DEFAULT_LANG).toLowerCase();
			setPayload(payloadOverride);
			if ((overrideLocale || DEFAULT_LANG) !== locale) {
				setLocale(overrideLocale || DEFAULT_LANG);
			}
			setLanguageOptions(languageOptionsOverride ?? buildLanguageOptions(overrideLocale || DEFAULT_LANG));
			return;
		}

		let cancelled = false;
		const load = async () => {
			setLoading(true);
			try {
				const targetLocale = (localeOverride || initialLocale || locale || DEFAULT_LANG).toLowerCase();
				const [layoutView, languagesView] = await Promise.all([
					client.content.apiContentSchemaKeyLanguageGet("layout", targetLocale).catch((err) => {
						console.error("Failed to load layout preview", err);
						return null;
					}),
					languageOptionsOverride
						? Promise.resolve(null)
						: client.content.apiContentSchemaKeyLanguageGet("languages", "default").catch((err) => {
								console.error("Failed to load languages for preview modal", err);
								return null;
						  })
				]);

				const previewPayload = layoutView?.data?.preview?.payload as SiteSettingsModel | null;
				const productionPayload = layoutView?.data?.production?.payload as SiteSettingsModel | null;
				const currentPayload = layoutView?.data?.current?.data as SiteSettingsModel | null;
				const layoutSource = previewPayload ?? productionPayload ?? currentPayload ?? null;
				const normalizedPayload = layoutSource
					? buildLayoutPayloadFromSettings(sanitizeLayoutSettings(layoutSource))
					: null;
				const layoutLocale =
					(layoutSource as any)?.language?.toString().toLowerCase() ||
					targetLocale ||
					DEFAULT_LANG;

				const languagesPayload =
					(languagesView?.data?.preview?.payload as LanguagesContent | null) ??
					(languagesView?.data?.production?.payload as LanguagesContent | null) ??
					(languagesView?.data?.current?.data as LanguagesContent | null) ??
					null;
				const optionsFromLanguages = languageOptionsOverride
					? languageOptionsOverride
					: buildOptionsFromLanguages(languagesPayload);
				const defaultLanguageFromContent =
					localeOverride ||
					normalizeCode(languagesPayload?.defaultLanguageCode) ||
					optionsFromLanguages[0]?.code ||
					DEFAULT_LANG;

				const resolvedLocale = normalizeCode(defaultLanguageFromContent) || layoutLocale || DEFAULT_LANG;
				const resolvedOptions =
					optionsFromLanguages.length > 0 ? optionsFromLanguages : buildLanguageOptions(resolvedLocale || DEFAULT_LANG);

				if (!cancelled) {
					setPayload(normalizedPayload);
					if ((resolvedLocale || DEFAULT_LANG) !== locale) {
						setLocale(resolvedLocale || DEFAULT_LANG);
					}
					setLanguageOptions(resolvedOptions);
				}
			} catch (err) {
				console.error("Failed to load layout preview", err);
				if (!cancelled) {
					setPayload(null);
					setLanguageOptions(languageOptionsOverride ?? buildLanguageOptions(locale || DEFAULT_LANG));
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [show, initialLocale, payloadOverride, localeOverride, languageOptionsOverride]);

	const language = locale || DEFAULT_LANG;
	return (
		<Modal show={show} onHide={onClose} size="full85" centered>
			<Modal.Header
				closeButton
				className="rounded-t-xl border-none bg-gradient-to-r from-white via-slate-50 to-slate-100 px-6 py-5"
			>
				<div className="space-y-1">
					<Modal.Title className="text-xl font-semibold text-slate-900">Layout preview</Modal.Title>
					<p className="text-sm text-slate-500">Review navigation and footer drafts on realistic devices.</p>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-slate-50/80" scrollable>
				<div className="flex flex-col gap-6 lg:flex-row">
					<aside className="flex flex-col gap-4 lg:w-80">
						<div className="space-y-3 text-sm text-slate-600">
							{disabled && (
								<div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
									<FiInfo className="mt-0.5 shrink-0" /> This locale is disabled. Visitors will not see the layout until it is
									re-enabled.
								</div>
							)}
							{loading && (
								<div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
									<FiInfo className="mt-0.5 shrink-0" /> Fetching the latest layout…
								</div>
							)}
							{!previewReady && !loading && (
								<div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
									<FiInfo className="mt-0.5 shrink-0" /> Add navigation and footer content to generate a preview.
								</div>
							)}
						</div>

						<div className="space-y-4 rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Viewport</p>
									<p className="text-[13px] text-slate-500">Simulate responsive widths</p>
								</div>
								<span className="text-xs font-mono text-slate-400">{viewportWidthLabel}</span>
							</div>
							<div className="flex flex-col gap-2">
								{VIEWPORT_ORDER.map((key) => {
									const config = PREVIEW_VIEWPORTS[key];
									const Icon = config.icon;
									const isActive = viewport === key;
									return (
										<button
											key={key}
											type="button"
											className={cx(
												"flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005CA9]/40",
												isActive
													? "border-[#005CA9] bg-[#005CA9]/10 text-[#0B3866]"
													: "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
											)}
											aria-pressed={isActive}
											onClick={() => setViewport(key)}
										>
											<span className="inline-flex items-center gap-2">
												<span
													className={cx(
														"flex h-7 w-7 items-center justify-center rounded-full border text-base",
														isActive
															? "border-[#005CA9]/40 bg-[#005CA9]/10 text-[#005CA9]"
															: "border-slate-200 bg-white text-slate-500"
													)}
													aria-hidden="true"
												>
													<Icon />
												</span>
												{config.label}
											</span>
											<span className="text-[11px] text-slate-400">{config.helper}</span>
										</button>
									);
								})}
							</div>
						</div>

						{isFullViewport && (
							<div className="space-y-3 rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-sm">
								<div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-600">
									<span>Full width controls</span>
									<button
										type="button"
										className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 transition hover:border-slate-300"
										onClick={() => {
											if (fullWidthAuto) {
												setFullWidthAuto(false);
											} else {
												setFullWidthAuto(true);
												setFullWidthPixels(FULL_WIDTH_RANGE.max);
											}
										}}
									>
										{fullWidthAuto ? "Enable custom width" : "Fit to modal"}
									</button>
								</div>
								<div className="text-xs font-mono text-slate-400">
									{fullWidthAuto ? "Using full modal width" : viewportWidthLabel}
								</div>
								{fullWidthAuto ? (
									<p className="text-[11px] text-slate-500">
										Turn on custom width to simulate smaller desktop canvases.
									</p>
								) : (
									<>
										<input
											type="range"
											min={FULL_WIDTH_RANGE.min}
											max={FULL_WIDTH_RANGE.max}
											step={FULL_WIDTH_RANGE.step}
											value={fullWidthPixels}
											onChange={(event) => {
												setFullWidthPixels(Number(event.target.value));
											}}
											className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-[#005CA9]"
											aria-label="Adjust full-width preview size"
										/>
										<div className="flex items-center justify-between text-[11px] font-medium text-slate-400">
											<span>{FULL_WIDTH_RANGE.min}px</span>
											<span>{FULL_WIDTH_RANGE.max}px</span>
										</div>
									</>
								)}
							</div>
						)}

						{isMobileViewport && (
							<div className="space-y-3 rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-sm">
								<div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-600">
									<span>Phone profiles</span>
									<span className="font-normal normal-case text-slate-400">Preset widths</span>
								</div>
								<div className="flex flex-col gap-2">
									{(Object.keys(MOBILE_PHONE_PRESETS) as MobilePresetKey[]).map((key) => {
										const preset = MOBILE_PHONE_PRESETS[key];
										const isActive = mobilePreset === key;
										return (
											<button
												key={key}
												type="button"
												className={cx(
													"flex items-center justify-between rounded-2xl border px-3 py-2 text-xs font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#005CA9]/40",
													isActive
														? "border-[#005CA9] bg-white text-[#0B3866] shadow-sm"
														: "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
												)}
												aria-pressed={isActive}
												onClick={() => setMobilePreset(key)}
											>
												<span>{preset.label}</span>
												<span className="text-[10px] font-mono text-slate-400">{preset.width}px</span>
											</button>
										);
									})}
								</div>
							</div>
						)}
					</aside>

					<div className="flex-1">
						<LayoutPreviewSurface
							headerData={headerData}
							footerData={footerData}
							language={language}
							languageOptions={languageOptions}
							defaultLanguageCode={language}
							viewport={displayViewport}
							width={viewportWidth}
						/>
					</div>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default LayoutPreviewModal;


